<template>
    <div class="ppv2-space-y-6">
        <!-- Form Description -->
        <div class="ppv2-mb-6">
            <p class="ppv2-text-sm ppv2-text-gray-600">
                {{ $t('configuration.contacts.formDescription') }}
            </p>
        </div>

        <!-- E-mail de contact client -->
        <BaseInput
            v-model="formData.email_contact_client"
            :label="$t('configuration.contacts.fields.email_contact_client.label')"
            :tooltipMsg="$t('configuration.contacts.fields.email_contact_client.description')"
            :placeholder="$t('configuration.contacts.fields.email_contact_client.placeholder')"
            type="email"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- E-mail de contact administrateur -->
        <BaseInput
            v-model="formData.email"
            :label="$t('configuration.contacts.fields.email.label')"
            :tooltipMsg="$t('configuration.contacts.fields.email.description')"
            :placeholder="$t('configuration.contacts.fields.email.placeholder')"
            type="email"
            :error="errors.email"
            :state="errors.email ? 'error' : 'default'"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Téléphone principal -->
        <BaseInput
            v-model="formData.phone_number"
            :label="$t('configuration.contacts.fields.phone_number.label')"
            :tooltipMsg="$t('configuration.contacts.fields.phone_number.description')"
            :placeholder="$t('configuration.contacts.fields.phone_number.placeholder')"
            type="tel"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Téléphone secondaire -->
        <BaseInput
            v-model="formData.phone_number2"
            :label="$t('configuration.contacts.fields.phone_number2.label')"
            :tooltipMsg="$t('configuration.contacts.fields.phone_number2.description')"
            :placeholder="$t('configuration.contacts.fields.phone_number2.placeholder')"
            type="tel"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Numéro WhatsApp -->
        <BaseInput
            v-model="formData.phone_number_whatsapp"
            :label="$t('configuration.contacts.fields.phone_number_whatsapp.label')"
            :tooltipMsg="$t('configuration.contacts.fields.phone_number_whatsapp.description')"
            :placeholder="$t('configuration.contacts.fields.phone_number_whatsapp.placeholder')"
            type="tel"
        >
            <template #leftIcon>
                <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_416_7738)">
                        <path
                            d="M2.42823 21.7171L3.72073 16.4536C0.215399 11.7529 1.6784 4.97693 6.79523 2.14994C12.6986 -1.11157 20.0704 2.5221 21.0787 9.15143C22.2209 16.6608 14.7794 22.4578 7.77607 19.6088L2.43007 21.7171H2.42823ZM18.0721 17.2016C23.4566 11.8189 20.2794 2.38094 12.7499 1.44227C4.42473 0.402768 -0.838768 9.75277 4.17357 16.3821L3.05157 20.9673L7.76873 19.1578C11.2484 20.6629 15.4027 19.8673 18.0702 17.2016H18.0721Z"
                            fill="#131C31"
                        />
                        <path
                            d="M4.73877 18.9085L5.53627 15.9513C1.6826 11.6118 3.70294 4.59385 9.2946 3.02451C15.4601 1.29568 21.0261 7.06701 19.0589 13.1628C17.5391 17.8745 12.0831 19.9462 7.70694 17.7223L4.73877 18.9067V18.9085ZM9.65577 6.43818C8.7996 6.22918 8.30827 6.36668 7.76927 7.05601C6.79394 8.30451 7.79127 10.5613 8.59244 11.7145C9.7181 13.3352 12.1088 15.1887 14.1309 15.35C15.2401 15.438 15.8139 15.0237 16.3951 14.1198L16.2851 13.0712L13.8779 12.1637C13.5553 12.1637 12.8879 13.1262 12.6588 13.0767C12.4424 13.029 11.3516 12.1288 11.1334 11.9198C10.8749 11.6742 9.68877 10.3322 9.66677 10.0718C9.64477 9.81151 10.5834 9.20651 10.5486 8.82885L9.65577 6.43818Z"
                            fill="#131C31"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_416_7738">
                            <rect width="22" height="22" fill="white" transform="translate(0.5 0.5)" />
                        </clipPath>
                    </defs>
                </svg>
            </template>
        </BaseInput>

        <div class="ppv2-w-full">
            <!-- Info Box -->
            <InfoMessageBox class="ppv2-mb-6" :message="$t('configuration.contacts.visibilityBox.help')" />

            <!-- Visibilité des numéros de contact -->
            <BaseRadioGroup
                v-model="formData.visibility_contact_phone"
                :label="$t('configuration.contacts.visibilityBox.title')"
                :description="$t('configuration.contacts.visibilityBox.description')"
                :options="visibilityOptions"
                :required="true"
            />
        </div>
    </div>
</template>

<script>
import BaseInput from '../../../Shared/BaseInput.vue'
import InfoMessageBox from '../../../Shared/InfoMessageBox.vue'
import BaseRadioGroup from '../../../Shared/BaseRadioGroup.vue'

export default {
    name: 'ContactsForm',
    components: {
        BaseInput,
        InfoMessageBox,
        BaseRadioGroup,
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
    },
    data() {
        return {
            formData: {
                email_contact_client: '',
                email: '',
                phone_number: '',
                phone_number2: '',
                phone_number_whatsapp: '',
                visibility_contact_phone: true,
            },
            errors: {},
            visibilityOptions: [
                {
                    value: true,
                    label: this.$t('configuration.contacts.visibilityBox.options.show.label'),
                    description: ['configuration.contacts.visibilityBox.options.show.desc'],
                },
                {
                    value: false,
                    label: this.$t('configuration.contacts.visibilityBox.options.hide.label'),
                    description: ['configuration.contacts.visibilityBox.options.hide.desc'],
                },
            ],
        }
    },
    created() {
        this.initializeForm()
    },
    watch: {
        establishment: {
            handler() {
                this.initializeForm()
            },
            immediate: true,
            deep: true,
        },
        formData: {
            handler(newVal) {
                this.$emit('update', newVal)
            },
            deep: true,
        },
    },
    methods: {
        initializeForm() {
            const fields = [
                'email_contact_client',
                'email',
                'phone_number',
                'phone_number2',
                'phone_number_whatsapp',
                'visibility_contact_phone',
            ]
            fields.forEach((field) => {
                if (this.establishment[field] !== undefined) {
                    this.formData[field] = this.establishment[field]
                }
            })
        },
        getFormData() {
            return this.formData
        },
        setFormData(data) {
            this.formData = { ...this.formData, ...data }
        },
    },
}
</script>

<style scoped>
input[type='radio'] {
    /* Hide default radio button */
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-radius: 50%;
    width: 1.25rem; /* w-5 */
    height: 1.25rem; /* h-5 */
    border: 2px solid #d1d5db; /* border-gray-300 */
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    position: relative;
    flex-shrink: 0; /* Prevent shrinking in flex containers */
}

input[type='radio']:checked {
    border-color: #7d2dbd;
    background-color: #7d2dbd;
}

input[type='radio']:checked::after {
    content: '';
    display: block;
    width: 0.5rem; /* w-2 */
    height: 0.5rem; /* h-2 */
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

input[type='radio']:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.5); /* ring-2 and ring-orange-500 with opacity */
}
</style>
